import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_URL = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 30000, // 30 second timeout
});

// Response interceptor to handle token expiration globally
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle 401 Unauthorized errors (token expired)
    if (error.response?.status === 401) {
      const errorDetail = error.response?.data?.detail || "";
      
      // Check if it's a token expiration error
      if (
        errorDetail.toLowerCase().includes("expired") ||
        errorDetail.toLowerCase().includes("invalid") ||
        errorDetail.toLowerCase().includes("token")
      ) {
        console.log("Token expired or invalid, clearing auth...");
        
        // Clear token from localStorage
        localStorage.removeItem("admin_token");
        
        // Only redirect if we're on an admin page
        if (window.location.pathname.startsWith("/admin") && 
            !window.location.pathname.includes("/login")) {
          // Store current path for redirect after login
          sessionStorage.setItem("redirect_after_login", window.location.pathname);
          
          // Redirect to login
          window.location.href = "/admin/login?expired=true";
        }
      }
    }
    
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  login: (data) => api.post("/auth/login", data),
  register: (data) => api.post("/auth/register", data),
};

// Homestay APIs
export const homestayAPI = {
  getAll: (params) => api.get("/homestays", { params }),
  getOne: (id) => api.get(`/homestays/${id}`),
  getBySlug: (slug) => api.get(`/homestays/by-slug/${slug}`),
  create: (data, token) =>
    api.post("/homestays", data, {
      headers: { Authorization: `Bearer ${token}` },
    }),
  update: (id, data, token) =>
    api.put(`/homestays/${id}`, data, {
      headers: { Authorization: `Bearer ${token}` },
    }),
  delete: (id, token) =>
    api.delete(`/homestays/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    }),
  uploadImages: (id, files, token) => {
    const formData = new FormData();
    files.forEach((file) => formData.append("files", file));
    return api.post(`/homestays/${id}/images`, formData, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "multipart/form-data",
      },
    });
  },
  uploadVideo: (id, file, token) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post(`/homestays/${id}/video`, formData, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "multipart/form-data",
      },
    });
  },
  deleteImage: (homestayId, imageIndex, token) =>
    api.delete(`/homestays/${homestayId}/images/${imageIndex}`, {
      headers: { Authorization: `Bearer ${token}` },
    }),
};

// Review APIs
export const reviewAPI = {
  getAll: (params) => api.get("/reviews", { params }),
  getAllAdmin: (token) =>
    api.get("/admin/reviews", {
      headers: { Authorization: `Bearer ${token}` },
    }),
  create: (data) => api.post("/reviews", data),
  approve: (id, token) =>
    api.put(`/reviews/${id}/approve`, null, {
      headers: { Authorization: `Bearer ${token}` },
    }),
  delete: (id, token) =>
    api.delete(`/reviews/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    }),
};

// Inquiry APIs
export const inquiryAPI = {
  create: (data) => api.post("/inquiries", data),
  getAll: (token) =>
    api.get("/admin/inquiries", {
      headers: { Authorization: `Bearer ${token}` },
    }),
  updateStatus: (id, status, token) =>
    api.put(`/inquiries/${id}/status?status=${status}`, null, {
      headers: { Authorization: `Bearer ${token}` },
    }),
};

// Dashboard Stats
export const statsAPI = {
  get: (token) =>
    api.get("/admin/stats", {
      headers: { Authorization: `Bearer ${token}` },
    }),
};

// Facilities
export const facilitiesAPI = {
  getAll: () => api.get("/facilities"),
};

// Location APIs
export const locationAPI = {
  getAll: (params) => api.get("/locations", { params }),
  getOne: (id) => api.get(`/locations/${id}`),
  getBySlug: (slug) => api.get(`/locations/by-slug/${slug}`),
  create: (data, token) =>
    api.post("/locations", data, {
      headers: { Authorization: `Bearer ${token}` },
    }),
  update: (id, data, token) =>
    api.put(`/locations/${id}`, data, {
      headers: { Authorization: `Bearer ${token}` },
    }),
  delete: (id, token) =>
    api.delete(`/locations/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    }),
  seedPopular: (token) =>
    api.post("/locations/seed-popular", {}, {
      headers: { Authorization: `Bearer ${token}` },
    }),
};

// Sub-Location (Village) APIs
export const subLocationAPI = {
  getAll: (params) => api.get("/sub-locations", { params }),
  getOne: (id) => api.get(`/sub-locations/${id}`),
  create: (data, token) =>
    api.post("/sub-locations", data, {
      headers: { Authorization: `Bearer ${token}` },
    }),
  update: (id, data, token) =>
    api.put(`/sub-locations/${id}`, data, {
      headers: { Authorization: `Bearer ${token}` },
    }),
  delete: (id, token) =>
    api.delete(`/sub-locations/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    }),
};

// Availability Calendar APIs
export const availabilityAPI = {
  get: (homestayId, params) => api.get(`/homestays/${homestayId}/availability`, { params }),
  set: (homestayId, dates, token) =>
    api.post(`/homestays/${homestayId}/availability`, dates, {
      headers: { Authorization: `Bearer ${token}` },
    }),
  delete: (homestayId, date, token) =>
    api.delete(`/homestays/${homestayId}/availability/${date}`, {
      headers: { Authorization: `Bearer ${token}` },
    }),
};

// Export APIs
export const exportAPI = {
  homestays: (token) =>
    api.get("/export/homestays", {
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob'
    }),
  inquiries: (token) =>
    api.get("/export/inquiries", {
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob'
    }),
  reviews: (token) =>
    api.get("/export/reviews", {
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob'
    }),
};

// Places of Interest APIs
export const placeAPI = {
  getAll: (params) => api.get("/places", { params }),
  getOne: (id) => api.get(`/places/${id}`),
  getBySlug: (slug) => api.get(`/places/by-slug/${slug}`),
  getNearby: (params) => api.get("/places/nearby", { params }),
  getSimilar: (id, limit = 6) => api.get(`/places/similar/${id}`, { params: { limit } }),
  getCategories: () => api.get("/places/categories"),
  create: (data, token) =>
    api.post("/places", data, {
      headers: { Authorization: `Bearer ${token}` },
    }),
  update: (id, data, token) =>
    api.put(`/places/${id}`, data, {
      headers: { Authorization: `Bearer ${token}` },
    }),
  delete: (id, token) =>
    api.delete(`/places/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    }),
  uploadImage: (id, file, token) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post(`/places/${id}/image`, formData, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "multipart/form-data",
      },
    });
  },
};

// Blog APIs
export const blogAPI = {
  getAll: (params) => api.get("/blog", { params }),
  getAllAdmin: (token) =>
    api.get("/blog/admin", {
      headers: { Authorization: `Bearer ${token}` },
    }),
  getOne: (id) => api.get(`/blog/${id}`),
  getBySlug: (slug) => api.get(`/blog/by-slug/${slug}`),
  create: (data, token) =>
    api.post("/blog", data, {
      headers: { Authorization: `Bearer ${token}` },
    }),
  update: (id, data, token) =>
    api.put(`/blog/${id}`, data, {
      headers: { Authorization: `Bearer ${token}` },
    }),
  delete: (id, token) =>
    api.delete(`/blog/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    }),
  uploadImage: (id, file, token) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post(`/blog/${id}/image`, formData, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "multipart/form-data",
      },
    });
  },
};

// Author APIs
export const authorsAPI = {
  getAll: () => api.get("/authors"),
  getOne: (id) => api.get(`/authors/${id}`),
  create: (data, token) =>
    api.post("/authors", data, {
      headers: { Authorization: `Bearer ${token}` },
    }),
  update: (id, data, token) =>
    api.put(`/authors/${id}`, data, {
      headers: { Authorization: `Bearer ${token}` },
    }),
  delete: (id, token) =>
    api.delete(`/authors/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    }),
  uploadPhoto: (id, file, token) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post(`/authors/${id}/photo`, formData, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "multipart/form-data",
      },
    });
  },
};

// Site Settings APIs
export const settingsAPI = {
  get: () => api.get("/settings"),
  update: (data, token) =>
    api.put("/settings", data, {
      headers: { Authorization: `Bearer ${token}` },
    }),
};

// Data Migration APIs
export const migrationAPI = {
  getUnassignedHomestays: (token) =>
    api.get("/admin/homestays/unassigned", {
      headers: { Authorization: `Bearer ${token}` },
    }),
  bulkAssign: (homestayIds, subLocationId, token) =>
    api.post("/admin/homestays/bulk-assign", null, {
      params: { homestay_ids: homestayIds, sub_location_id: subLocationId },
      headers: { Authorization: `Bearer ${token}` },
    }),
  assignToVillage: (homestayId, subLocationId, token) =>
    api.post(`/admin/homestays/${homestayId}/assign-village`, null, {
      params: { sub_location_id: subLocationId },
      headers: { Authorization: `Bearer ${token}` },
    }),
};

// Place Review APIs
export const placeReviewAPI = {
  getApproved: (placeId) => api.get(`/places/${placeId}/reviews`),
  submit: (placeId, data) => api.post(`/places/${placeId}/reviews`, data),
  getPending: (token) =>
    api.get("/place-reviews/pending", { headers: { Authorization: `Bearer ${token}` } }),
  getAll: (token) =>
    api.get("/place-reviews/all", { headers: { Authorization: `Bearer ${token}` } }),
  approve: (id, token) =>
    api.put(`/place-reviews/${id}/approve`, {}, { headers: { Authorization: `Bearer ${token}` } }),
  reject: (id, token) =>
    api.put(`/place-reviews/${id}/reject`, {}, { headers: { Authorization: `Bearer ${token}` } }),
  delete: (id, token) =>
    api.delete(`/place-reviews/${id}`, { headers: { Authorization: `Bearer ${token}` } }),
};

// SEO Collection APIs
export const seoCollectionAPI = {
  getAll: (token) =>
    api.get("/seo-collections", {
      headers: { Authorization: `Bearer ${token}` },
    }),
  getOne: (id, token) =>
    api.get(`/seo-collections/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    }),
  getPublic: () => api.get("/seo-collections/public"),
  getPublicBySlug: (slug) => api.get(`/seo-collections/public/${slug}`),
  create: (data, token) =>
    api.post("/seo-collections", data, {
      headers: { Authorization: `Bearer ${token}` },
    }),
  update: (id, data, token) =>
    api.put(`/seo-collections/${id}`, data, {
      headers: { Authorization: `Bearer ${token}` },
    }),
  delete: (id, token) =>
    api.delete(`/seo-collections/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    }),
};

export default api;
